import { useCallback, useEffect, useState } from "react";
import { fetchPegStatus } from "../services/api.js";

/**
 * Returns the live USDC peg status from the backend (which reads
 * DePegGuard + BondContract.pegOk() + Chainlink server-side). Frontend
 * never touches the chain directly per the data-flow spec.
 */
export function usePegStatus({ pollMs = 30_000 } = {}) {
    const [status, setStatus] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const api = await fetchPegStatus();
            setStatus({
                isPegged: Boolean(api.isPegged),
                lastObservedPrice: BigInt(api.lastObservedPrice || "0"),
                deviationBps: Number(api.deviationBps || 0),
                source: "api"
            });
            setError(null);
        } catch (err) {
            setError(err?.message || "Failed to fetch peg status");
            // Keep the last good status on a transient poll failure so the
            // peg indicator doesn't blink to empty between good reads.
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        if (!pollMs) return undefined;
        const handle = setInterval(refresh, pollMs);
        return () => clearInterval(handle);
    }, [refresh, pollMs]);

    return { status, isLoading, error, refresh };
}
